"use client";

import { useEffect, useState } from "react";
import {
  CONSENT_STORAGE_KEY,
  CONSENT_OPEN_EVENT,
  CONSENT_UPDATED_EVENT,
  ConsentStatus,
  CookieConsent,
  acceptAllConsent,
  defaultConsent,
  essentialOnlyConsent,
  loadConsent,
  saveConsent
} from "../lib/consent";
import { AnalyticsGate } from "./AnalyticsGate";
import { CookieBanner } from "./CookieBanner";
import { CookiePreferencesModal } from "./CookiePreferencesModal";

export function AnalyticsConsentManager(): JSX.Element {
  const [status, setStatus] = useState<ConsentStatus>("unknown");
  const [preferences, setPreferences] = useState<CookieConsent>(defaultConsent());
  const [ready, setReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const { consent, exists } = loadConsent();
    setPreferences(consent);
    setStatus(exists ? "saved" : "unknown");

    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready || typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== CONSENT_STORAGE_KEY) {
        return;
      }

      const { consent, exists } = loadConsent();
      setPreferences(consent);
      setStatus(exists ? "saved" : "unknown");
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [ready]);

  useEffect(() => {
    if (!ready || typeof window === "undefined") {
      return;
    }

    const handleConsentUpdate = (): void => {
      const { consent, exists } = loadConsent();
      setPreferences(consent);
      setStatus(exists ? "saved" : "unknown");
    };

    window.addEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);
    return () => window.removeEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);
  }, [ready]);

  useEffect(() => {
    if (!ready || typeof window === "undefined") {
      return;
    }

    const handleOpen = (): void => setModalOpen(true);
    window.addEventListener(CONSENT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, handleOpen);
  }, [ready]);

  const applyConsent = (prefs: CookieConsent): void => {
    const record = saveConsent(prefs);
    setPreferences(record);
    setStatus("saved");
    setModalOpen(false);
  };

  const acceptAll = (): void => applyConsent(acceptAllConsent());
  const rejectAll = (): void => applyConsent(essentialOnlyConsent());

  const showBanner = ready && status === "unknown" && !modalOpen;

  return (
    <>
      <AnalyticsGate consentStatus={status} preferences={preferences} ready={ready} />
      <CookieBanner
        visible={showBanner}
        onAccept={acceptAll}
        onReject={rejectAll}
        onCustomize={() => setModalOpen(true)}
      />
      <CookiePreferencesModal
        open={modalOpen}
        initialPreferences={preferences}
        onClose={() => setModalOpen(false)}
        onSave={applyConsent}
        onAcceptAll={acceptAll}
        onRejectAll={rejectAll}
      />
    </>
  );
}
